import { accountingApi } from '../config/apiClient';
import { ACCESS_CONTROL_API } from '../config/api';

/** Kapının fiziksel yönü. Boş = henüz yapılandırılmadı (karar üretilemez). */
export type DoorDirection = 'entry' | 'exit' | null;

export interface AccessDoor {
  id: number;
  doorNumber: number;
  name: string | null;
  direction: DoorDirection;
  location: string | null;
  isActive: boolean;
}

export interface AccessPanel {
  id: number;
  deviceKey: string;
  ipAddress: string | null;
  name: string | null;
  location: string | null;
  branchId: string | null;
  doorCount: number;
  lastSeenAt: string | null;
  isActive: boolean;
  unconfiguredDoorCount: number;
  doors: AccessDoor[];
}

export interface AccessAgent {
  id: number;
  screenCode: string;
  name: string | null;
  machineId: string | null;
  instanceCode: string | null;
  lastSeenAt: string | null;
  lastSyncAt: string | null;
  isActive: boolean;
  panels: AccessPanel[];
  unconfiguredDoorCount: number;
}

export interface AccessPanelsResponse {
  success: boolean;
  /** Kiracıda tablolar henüz açılmamışsa true döner; liste boştur. */
  tablesMissing: boolean;
  data: AccessAgent[];
  totals: {
    agents: number;
    panels: number;
    doors: number;
    unconfiguredDoors: number;
  };
}

export interface AccessProfile {
  id: number;
  name: string;
  description: string | null;
  color: string | null;
  isActive: boolean;
  doorCount: number;
  cardCount: number;
  createdAt: string;
}

export interface AccessProfilesResponse {
  success: boolean;
  tablesMissing: boolean;
  data: AccessProfile[];
}

/** Kartın atandığı profilin çip gösterimi için gereken asgari bilgi. */
export interface CardProfileChip {
  id: number;
  name: string;
  color: string | null;
}

export interface AccessCard {
  id: number;
  cardNumber: string;
  /** ACCOUNT (cari) veya PERSONNEL (personel). */
  ownerType: 'ACCOUNT' | 'PERSONNEL';
  /** Sahiplik türüne göre çözülmüş ad: cari adı ya da personel adı. */
  accountName: string;
  cardUID: string | null;
  hasTurnstileUID: boolean;
  isActive: boolean;
  profiles: CardProfileChip[];
}

export interface AccessCardsResponse {
  success: boolean;
  tablesMissing: boolean;
  data: AccessCard[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export type PassLogStatus = 'ALLOWED' | 'DENIED';

export interface AccessPassLog {
  id: number;
  agentId: number | null;
  cardUID: string;
  cardId: number | null;
  cardLabel: string | null;
  panelDeviceKey: string;
  doorNumber: number;
  doorName: string | null;
  direction: DoorDirection;
  eventTimestamp: string;
  status: PassLogStatus;
  /** Ret gerekçesi; izin verilip röle tetiklenemediğinde de not olarak dolar. */
  denialReason: string | null;
  decidedOffline: boolean;
}

export interface AccessPassLogsResponse {
  success: boolean;
  tablesMissing: boolean;
  data: AccessPassLog[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface PassLogFilters {
  page?: number;
  pageSize?: number;
  startDate?: string;
  endDate?: string;
  agentId?: number;
  deviceKey?: string;
  doorNumber?: number;
  status?: string;
  search?: string;
}

export const accessControlService = {
  getPanels: () => accountingApi.get<AccessPanelsResponse>(ACCESS_CONTROL_API.PANELS),

  updatePanel: (data: { id: number; name?: string | null; location?: string | null; branchId?: string | null; doorCount?: number }) =>
    accountingApi.post(ACCESS_CONTROL_API.PANEL_UPDATE, data),

  updateDoor: (data: { id: number; name?: string | null; direction?: DoorDirection; location?: string | null; isActive?: boolean }) =>
    accountingApi.post(ACCESS_CONTROL_API.DOOR_UPDATE, data),

  // ─── Profiller ───
  getProfiles: () => accountingApi.get<AccessProfilesResponse>(ACCESS_CONTROL_API.PROFILES),

  createProfile: (data: { name: string; description?: string | null; color?: string | null }) =>
    accountingApi.post(ACCESS_CONTROL_API.PROFILE_CREATE, data),

  updateProfile: (data: { id: number; name: string; description?: string | null; color?: string | null; isActive?: boolean }) =>
    accountingApi.post(ACCESS_CONTROL_API.PROFILE_UPDATE, data),

  deleteProfile: (id: number) =>
    accountingApi.post<{ success: boolean; affectedCards: number; affectedDoors: number }>(ACCESS_CONTROL_API.PROFILE_DELETE, { id }),

  getProfileDoors: (profileId: number) =>
    accountingApi.get<{ success: boolean; tablesMissing: boolean; doorIds: number[] }>(ACCESS_CONTROL_API.PROFILE_DOORS(profileId)),

  /** Tam liste ile değiştirir: gönderilmeyen kapılar profilden düşer. */
  setProfileDoors: (profileId: number, doorIds: number[]) =>
    accountingApi.post<{ success: boolean; added: number; removed: number; doorsWithoutDirection: number }>(
      ACCESS_CONTROL_API.PROFILE_DOORS_SET, { profileId, doorIds }),

  // ─── Kart yetkileri ───
  getCards: (params?: { page?: number; pageSize?: number; search?: string; profileId?: number; unassignedOnly?: boolean }) =>
    accountingApi.get<AccessCardsResponse>(ACCESS_CONTROL_API.CARDS(params)),

  assignCardProfiles: (cardIds: number[], profileId: number) =>
    accountingApi.post<{ success: boolean; assigned: number; skipped: number }>(
      ACCESS_CONTROL_API.CARD_PROFILES_ASSIGN, { cardIds, profileId }),

  removeCardProfiles: (cardIds: number[], profileId: number) =>
    accountingApi.post<{ success: boolean; removed: number }>(
      ACCESS_CONTROL_API.CARD_PROFILES_REMOVE, { cardIds, profileId }),

  /**
   * Panel bilgisayarına anlık senkron tetiği gönderir. <c>reached=false</c> HATA DEĞİLDİR:
   * panel çevrimdışıysa en geç 60 saniyede kendisi senkronlanır.
   */
  pushSyncToAgent: (agentId: number) =>
    accountingApi.post<{ success: boolean; reached: boolean; delivered?: boolean; code?: string; message: string }>(
      ACCESS_CONTROL_API.AGENT_PUSH_SYNC, { id: agentId }),

  // ─── Geçiş kayıtları ───
  getPassLogs: (params?: PassLogFilters) =>
    accountingApi.get<AccessPassLogsResponse>(ACCESS_CONTROL_API.PASS_LOGS(params)),
};

export default accessControlService;
